import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import StewartInput from "controls/global/stewart-input";
import { PremiumReceivablesAgentRecord } from "entities/UIModel/PremiumReceivablesAgentRecord";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import { excelDownload, downloadFile } from "utils/download-files/excelDownload";
import { formatDate } from "../../policy-management/shared/policyColumnHelpers";
import { formatPeriod } from "../shared/formatters";
import { GroupBy, DetailLevel, groupByLabel } from "./ledgerReportTypes";

export interface LedgerBlock {
  agent: PremiumReceivablesAgentRecord;
  beginBal: number;
  periodPR: PremiumReceivablesTransactionRecord[];
  periodAR: PremiumReceivablesReceiptRecord[];
  totalCharges: number;
  totalPayments: number;
  endBal: number;
}

const ReportHeader = styled(Box)({
  padding: padding.large1,
  backgroundColor: colors.blue15,
  color: colors.white,
  borderTopLeftRadius: borderRadius.small,
  borderTopRightRadius: borderRadius.small,
});

const ActionsBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  justifyContent: "flex-end",
  flexWrap: "wrap",
});

const ChipStrip = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: gaps.medium1,
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderTop: "none",
  padding: padding.medium1,
  "& .chip": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: padding.small1,
  },
  "& .label": {
    fontSize: fontSize.xsmall,
    color: colors.grey11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  "& .value": {
    fontSize: fontSize.xlarge,
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
  },
});

const ReportBodyGrid = styled(Box)({
  display: "flex",
  gap: gaps.medium1,
  alignItems: "flex-start",
});

const NavRail = styled(Box)({
  width: "220px",
  flex: "0 0 220px",
  position: "sticky",
  top: "8px",
  maxHeight: "calc(100vh - 200px)",
  overflowY: "auto",
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  "& .nav-header": {
    padding: padding.small1,
    fontSize: fontSize.xsmall,
    fontWeight: fontWeight.bold2,
    color: colors.grey11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: `1px solid ${colors.grey09}`,
  },
  "& .nav-item": {
    padding: `${padding.small1} ${padding.medium1}`,
    fontSize: fontSize.small,
    cursor: "pointer",
    borderBottom: `1px solid ${colors.grey12}`,
    "&:hover": {
      backgroundColor: colors.blue08,
    },
  },
});

const ReportMain = styled(Box)({
  flex: "1 1 auto",
  minWidth: 0,
});

const GroupHeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `${padding.medium1} ${padding.large1}`,
  backgroundColor: "#eef2ff",
  border: `1px solid ${colors.grey09}`,
  marginTop: gaps.medium1,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
});

const BlockCard = styled(Box)({
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  overflow: "hidden",
  marginTop: gaps.small1,
  backgroundColor: colors.white,
});

const BlockHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: padding.medium1,
  backgroundColor: "#f1f5f9",
  borderBottom: `1px solid ${colors.grey09}`,
  gap: gaps.medium1,
  flexWrap: "wrap",
  cursor: "pointer",
});

const AddrGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(140px, auto))",
  columnGap: gaps.large1,
  rowGap: "2px",
  marginTop: "6px",
  "& .cell": {
    fontSize: fontSize.xsmall,
  },
  "& .cell .label": {
    color: colors.grey11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginRight: "4px",
  },
  "& .cell .value": {
    color: colors.grey08,
    fontWeight: fontWeight.normal2,
  },
  "& .cell .value.muted": {
    color: colors.grey14,
  },
});

const BalanceGrid = styled(Box)({
  display: "flex",
  gap: gaps.large1,
  flexWrap: "wrap",
  "& .item": {
    display: "flex",
    flexDirection: "column",
    minWidth: "100px",
  },
  "& .label": {
    fontSize: fontSize.xsmall,
    color: colors.grey11,
    fontWeight: fontWeight.normal2,
    textTransform: "uppercase",
  },
  "& .value": {
    fontSize: fontSize.base,
    fontWeight: fontWeight.bold2,
  },
});

const DetailTable = styled("table")({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.base,
  "& th, & td": {
    padding: `${padding.small1} ${padding.medium1}`,
    textAlign: "left",
    borderBottom: `1px solid ${colors.grey09}`,
  },
  "& th": {
    fontSize: fontSize.xsmall,
    color: colors.grey11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    backgroundColor: colors.grey10,
  },
  "& .section-header": {
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
    backgroundColor: "#eef2ff",
  },
  "& .total-row": {
    fontWeight: fontWeight.bold2,
    backgroundColor: colors.grey10,
  },
  "& .right": { textAlign: "right" },
  "& .muted": { color: colors.grey11 },
});

const GrandTotalsRow = styled(Box)({
  display: "flex",
  gap: gaps.large1,
  flexWrap: "wrap",
  padding: padding.large1,
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  marginTop: gaps.medium1,
  position: "sticky",
  bottom: 0,
  zIndex: 5,
  boxShadow: "0 -2px 6px rgba(0,0,0,0.08)",
  "& .item": {
    display: "flex",
    flexDirection: "column",
  },
  "& .label": {
    fontSize: fontSize.xsmall,
    color: colors.grey11,
    fontWeight: fontWeight.normal2,
    textTransform: "uppercase",
  },
  "& .value": {
    fontSize: fontSize.large,
    fontWeight: fontWeight.bold2,
    color: colors.blue15,
  },
});

function formatCurrency(value: number | undefined): string {
  if (value === undefined || value === null) return "$0.00";
  const prefix = value < 0 ? "-$" : "$";
  return (
    prefix +
    Math.abs(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function getBalanceColor(value: number): string {
  if (value < 0) return colors.green;
  if (value > 0) return colors.red01;
  return colors.grey11;
}

function buildCsv(headers: string[], rows: Record<string, string>[]): string {
  const escape = (v: string) => `"${(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => escape(r[h] ?? "")).join(",")));
  return lines.join("\n");
}

function blockToExportRows(block: LedgerBlock): Record<string, string>[] {
  const rows: Record<string, string>[] = [];
  rows.push({
    Type: "AGENT",
    Agent: block.agent.agentID,
    Name: block.agent.name,
    Date: "",
    "Policy/Rcpt": "",
    Ref: "",
    Amount: String(block.endBal),
  });
  block.periodPR.forEach((t) => {
    rows.push({
      Type: "CHARGE",
      Agent: block.agent.agentID,
      Name: block.agent.name,
      Date: formatDate(t.ReceivedDate),
      "Policy/Rcpt": t.PolicyNumber || "",
      Ref: formatPeriod(t.AccountingPeriod),
      Amount: String(t.ActualRemittance ?? 0),
    });
  });
  block.periodAR.forEach((r) => {
    rows.push({
      Type: "PAYMENT",
      Agent: block.agent.agentID,
      Name: block.agent.name,
      Date: formatDate(r.ReceiptDate),
      "Policy/Rcpt": r.ReceiptNumber || "",
      Ref: r.ReceiptTypeCode || "",
      Amount: String(r.ReceiptAmount),
    });
  });
  return rows;
}

interface LedgerReportBodyProps {
  ledgerData: LedgerBlock[] | null;
  groupBy: GroupBy;
  detailLevel: DetailLevel;
  subtitle: string;
}

const LedgerReportBody = ({ ledgerData, groupBy, detailLevel, subtitle }: LedgerReportBodyProps) => {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const grandTotals = useMemo(() => {
    if (!ledgerData) return null;
    return {
      agents: ledgerData.length,
      beginBal: ledgerData.reduce((s, b) => s + b.beginBal, 0),
      totalCharges: ledgerData.reduce((s, b) => s + b.totalCharges, 0),
      totalPayments: ledgerData.reduce((s, b) => s + b.totalPayments, 0),
      endBal: ledgerData.reduce((s, b) => s + b.endBal, 0),
    };
  }, [ledgerData]);

  const chipTotals = useMemo(() => {
    if (!ledgerData) return { agents: 0, registers: 0, receipts: 0, outstanding: 0 };
    return {
      agents: ledgerData.length,
      registers: ledgerData.reduce((s, b) => s + b.periodPR.length, 0),
      receipts: ledgerData.reduce((s, b) => s + b.periodAR.length, 0),
      outstanding: ledgerData.reduce((s, b) => s + b.endBal, 0),
    };
  }, [ledgerData]);

  const searchedData = useMemo(() => {
    if (!ledgerData) return null;
    const term = search.trim().toLowerCase();
    if (!term) return ledgerData;
    return ledgerData.filter((b) => {
      if (b.agent.agentID.toLowerCase().includes(term)) return true;
      if (b.agent.name.toLowerCase().includes(term)) return true;
      if (b.periodPR.some((t) => (t.PolicyNumber || "").toLowerCase().includes(term))) return true;
      if (b.periodAR.some((r) => (r.ReceiptNumber || "").toLowerCase().includes(term))) return true;
      return false;
    });
  }, [ledgerData, search]);

  const groupedBlocks = useMemo(() => {
    if (!searchedData) return null;
    if (groupBy === "agent" || groupBy === "all") {
      return { "": searchedData };
    }
    const key = groupBy as "state" | "division" | "region" | "district";
    const grouped: Record<string, LedgerBlock[]> = {};
    searchedData.forEach((b) => {
      const k = (b.agent[key as keyof PremiumReceivablesAgentRecord] as string) || "—";
      if (!grouped[k]) grouped[k] = [];
      grouped[k].push(b);
    });
    return grouped;
  }, [searchedData, groupBy]);

  const toggleCollapsed = useCallback((agentID: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(agentID)) next.delete(agentID);
      else next.add(agentID);
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => setCollapsed(new Set()), []);
  const handleCollapseAll = useCallback(() => {
    if (!ledgerData) return;
    setCollapsed(new Set(ledgerData.map((b) => b.agent.agentID)));
  }, [ledgerData]);

  const handleScrollToAgent = useCallback((agentID: string) => {
    const el = cardRefs.current.get(agentID);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleExportExcel = useCallback(() => {
    if (!ledgerData) return;
    const headers = ["Type", "Agent", "Name", "Date", "Policy/Rcpt", "Ref", "Amount"];
    const data: Record<string, string>[] = [];
    ledgerData.forEach((block) => data.push(...blockToExportRows(block)));
    excelDownload(headers, data, "pr_ledger.xlsx");
    setExportAnchor(null);
  }, [ledgerData]);

  const handleExportCsv = useCallback(() => {
    if (!ledgerData) return;
    const headers = ["Type", "Agent", "Name", "Date", "Policy/Rcpt", "Ref", "Amount"];
    const data: Record<string, string>[] = [];
    ledgerData.forEach((block) => data.push(...blockToExportRows(block)));
    const csv = buildCsv(headers, data);
    const blob = new Blob([csv], { type: "text/csv" });
    downloadFile(URL.createObjectURL(blob), "pr_ledger.csv");
    setExportAnchor(null);
  }, [ledgerData]);

  const handlePrint = useCallback(() => {
    window.print();
    setExportAnchor(null);
  }, []);

  const renderBlock = (block: LedgerBlock) => {
    const showDetail = detailLevel === "detail" && !collapsed.has(block.agent.agentID);
    return (
      <BlockCard
        key={block.agent.agentID}
        ref={(el: HTMLDivElement | null) => {
          if (el) cardRefs.current.set(block.agent.agentID, el);
        }}
      >
        <BlockHeader onClick={() => toggleCollapsed(block.agent.agentID)}>
          <Box sx={{ display: "flex", gap: gaps.small1 }}>
            {collapsed.has(block.agent.agentID) ? (
              <ChevronRightIcon sx={{ color: colors.grey11 }} />
            ) : (
              <ExpandMoreIcon sx={{ color: colors.grey11 }} />
            )}
            <Box>
              <Typography sx={{ fontSize: fontSize.large, fontWeight: fontWeight.bold2, color: colors.blue15 }}>
                {block.agent.agentID} · {block.agent.name}
              </Typography>
              <AddrGrid>
                <Box className="cell"><span className="label">Address:</span><span className="value muted">—</span></Box>
                <Box className="cell"><span className="label">Region:</span><span className="value">{block.agent.region || "—"}</span></Box>
                <Box className="cell"><span className="label">District:</span><span className="value">{block.agent.district || "—"}</span></Box>
                <Box className="cell"><span className="label">County:</span><span className="value muted">—</span></Box>
                <Box className="cell"><span className="label">Remittance:</span><span className="value">{block.agent.remitPct ? `${block.agent.remitPct.toFixed(1)}%` : "—"}</span></Box>
                <Box className="cell"><span className="label">Office:</span><span className="value">{block.agent.officeType || "—"}</span></Box>
              </AddrGrid>
              <Box sx={{ display: "flex", gap: "6px", marginTop: "4px" }}>
                {block.agent.isCancelled && (
                  <Box component="span" sx={{ fontSize: fontSize.xsmall, fontWeight: fontWeight.bold2, backgroundColor: colors.red01, color: colors.white, padding: "2px 6px", borderRadius: "3px" }}>
                    CANCELLED
                  </Box>
                )}
                {block.agent.isNonIssuing && (
                  <Box component="span" sx={{ fontSize: fontSize.xsmall, fontWeight: fontWeight.bold2, backgroundColor: "#f59e0b", color: colors.white, padding: "2px 6px", borderRadius: "3px" }}>
                    NON-ISSUING
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <BalanceGrid>
            <Box className="item">
              <Typography className="label">Beginning</Typography>
              <Typography className="value" sx={{ color: getBalanceColor(block.beginBal) }}>{formatCurrency(block.beginBal)}</Typography>
            </Box>
            <Box className="item">
              <Typography className="label">Charges</Typography>
              <Typography className="value" sx={{ color: colors.red01 }}>{formatCurrency(block.totalCharges)}</Typography>
            </Box>
            <Box className="item">
              <Typography className="label">Payments</Typography>
              <Typography className="value" sx={{ color: colors.green }}>{formatCurrency(block.totalPayments)}</Typography>
            </Box>
            <Box className="item">
              <Typography className="label">Ending</Typography>
              <Typography className="value" sx={{ color: getBalanceColor(block.endBal) }}>{formatCurrency(block.endBal)}</Typography>
              {(() => {
                const netChange = block.endBal - block.beginBal;
                if (Math.abs(netChange) < 0.01) return null;
                const sym = netChange > 0 ? "▲" : "▼";
                return (
                  <Typography sx={{ fontSize: fontSize.xsmall, color: getBalanceColor(netChange), fontWeight: fontWeight.bold2, marginTop: "2px" }}>
                    {sym} {formatCurrency(Math.abs(netChange))}
                  </Typography>
                );
              })()}
            </Box>
          </BalanceGrid>
        </BlockHeader>

        {showDetail && (
          <Box sx={{ padding: padding.medium1 }}>
            <DetailTable>
              <thead>
                <tr>
                  <th>Rcvd Date</th>
                  <th>Policy Yr/Mo</th>
                  <th>Tran</th>
                  <th className="right">Policies</th>
                  <th className="right">S.I.</th>
                  <th className="right">Liability</th>
                  <th className="right">S.I. Liability</th>
                  <th className="right">Gross Premium</th>
                  <th className="right">Premium Remittance</th>
                  <th className="right">UW Retention</th>
                  <th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="section-header" colSpan={11}>REGISTERS — PREMIUM RECEIVABLES</td>
                </tr>
                <tr>
                  <td colSpan={10} style={{ fontStyle: "italic", color: colors.grey11 }}>Balance Forward</td>
                  <td className="right" style={{ color: getBalanceColor(block.beginBal) }}>{formatCurrency(block.beginBal)}</td>
                </tr>
                {block.periodPR.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ fontStyle: "italic", color: colors.grey11, textAlign: "center" }}>
                      No charges in this period
                    </td>
                  </tr>
                ) : (
                  block.periodPR.map((t) => (
                    <tr key={t.uniqueIdentifier}>
                      <td>{formatDate(t.ReceivedDate)}</td>
                      <td className="muted" style={{ fontFamily: "monospace" }}>{formatPeriod(t.PolicyYearMonth)}</td>
                      <td>
                        <Box component="span" sx={{ fontSize: fontSize.xsmall, fontWeight: fontWeight.bold2, padding: "2px 6px", borderRadius: "3px", backgroundColor: t.IsAccrual ? "#3b82f6" : colors.red01, color: colors.white }}>
                          {t.PremiumReceivableTypeCode}
                        </Box>
                      </td>
                      <td className="right">{t.PolicyCount ?? 0}</td>
                      <td className="right">{t.SIPolicyCount ? t.SIPolicyCount.toLocaleString() : <span className="muted">—</span>}</td>
                      <td className="right">{t.LiabilityAmount ? formatCurrency(t.LiabilityAmount) : <span className="muted">—</span>}</td>
                      <td className="right">{t.SILiabilityAmount ? formatCurrency(t.SILiabilityAmount) : <span className="muted">—</span>}</td>
                      <td className="right">{t.ActualFee ? formatCurrency(t.ActualFee) : <span className="muted">—</span>}</td>
                      <td className="right muted">—</td>
                      <td className="right" style={{ color: colors.red01, fontWeight: fontWeight.bold2 }}>{formatCurrency(t.ActualRemittance ?? 0)}</td>
                      <td className="right muted">—</td>
                    </tr>
                  ))
                )}
                <tr className="total-row">
                  <td colSpan={10} style={{ color: colors.red01 }}>Register Totals</td>
                  <td className="right" style={{ color: colors.red01 }}>{formatCurrency(block.totalCharges)}</td>
                </tr>

                <tr>
                  <td className="section-header" colSpan={11}>RECEIPTS — ACCOUNTING RECEIVABLES</td>
                </tr>
                {block.periodAR.length === 0 ? (
                  <tr>
                    <td colSpan={11} style={{ fontStyle: "italic", color: colors.grey11, textAlign: "center" }}>
                      No payments in this period
                    </td>
                  </tr>
                ) : (
                  block.periodAR.map((r) => (
                    <tr key={r.uniqueIdentifier}>
                      <td>{formatDate(r.ReceiptDate)}</td>
                      <td className="muted" style={{ fontFamily: "monospace" }}>{`${r.ReceiptTypeCode} ${r.ReceiptNumber || ""}`.trim()}</td>
                      <td colSpan={8} style={{ fontStyle: "italic", color: colors.grey11 }}>{r.Comments || "—"}</td>
                      <td className="right" style={{ color: colors.green, fontWeight: fontWeight.bold2 }}>{formatCurrency(r.ReceiptAmount)}</td>
                    </tr>
                  ))
                )}
                <tr className="total-row">
                  <td colSpan={10} style={{ color: colors.green }}>Receipt Totals</td>
                  <td className="right" style={{ color: colors.green }}>{formatCurrency(block.totalPayments)}</td>
                </tr>

                <tr className="total-row">
                  <td colSpan={10}>Balance</td>
                  <td className="right" style={{ color: getBalanceColor(block.endBal) }}>{formatCurrency(block.endBal)}</td>
                </tr>
              </tbody>
            </DetailTable>
            <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11, marginTop: gaps.small1 }}>
              NOTE: S.I. = Number of Simultaneous Issue policies reported. Policies = total reported in batch.
            </Typography>
          </Box>
        )}
      </BlockCard>
    );
  };

  if (!ledgerData) {
    return (
      <Box sx={{ padding: padding.xlarge1, textAlign: "center", color: colors.grey11, border: `1px dashed ${colors.grey09}`, borderRadius: borderRadius.small, backgroundColor: colors.white }}>
        <Typography sx={{ fontSize: fontSize.base }}>
          Select your criteria and click <strong>Generate Ledger</strong> to build the report.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <ReportHeader>
        <Typography sx={{ fontSize: fontSize.xlarge, fontWeight: fontWeight.bold2 }}>
          Premium Receivables Ledger
        </Typography>
        <Typography sx={{ fontSize: fontSize.small, opacity: 0.9 }}>
          {subtitle || groupByLabel[groupBy]}
        </Typography>
      </ReportHeader>

      <ActionsBar sx={{ padding: padding.medium1, backgroundColor: colors.white, border: `1px solid ${colors.grey09}`, borderTop: "none" }}>
        <StewartInput
          name="lrSearch"
          placeholder="Agent, receipt #, policy..."
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          sx={{ minWidth: "240px" }}
        />
        <ActionButton variant="text" color="secondary" startIcon={<UnfoldMoreIcon />} onClick={handleExpandAll}>
          Expand All
        </ActionButton>
        <ActionButton variant="text" color="secondary" startIcon={<UnfoldLessIcon />} onClick={handleCollapseAll}>
          Collapse All
        </ActionButton>
        <ActionButton
          variant="outlined"
          color="secondary"
          startIcon={<FileDownloadIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(e: React.MouseEvent<HTMLElement>) => setExportAnchor(e.currentTarget)}
        >
          Export
        </ActionButton>
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={handleExportCsv}>Export to CSV</MenuItem>
          <MenuItem onClick={handleExportExcel}>Export to Excel</MenuItem>
          <MenuItem onClick={handlePrint}>Print to PDF</MenuItem>
        </Menu>
      </ActionsBar>

      {ledgerData.length === 0 ? (
        <Box sx={{ padding: padding.xlarge1, textAlign: "center", color: colors.grey11, border: `1px solid ${colors.grey09}`, borderTop: "none" }}>
          No agents match the selected criteria.
        </Box>
      ) : (
        <>
          <ChipStrip>
            <Box className="chip">
              <Typography className="label">Agents</Typography>
              <Typography className="value">{chipTotals.agents}</Typography>
              <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11 }}>in this report</Typography>
            </Box>
            <Box className="chip">
              <Typography className="label">Registers</Typography>
              <Typography className="value">{chipTotals.registers}</Typography>
              <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11 }}>premium entries</Typography>
            </Box>
            <Box className="chip">
              <Typography className="label">Receipts</Typography>
              <Typography className="value">{chipTotals.receipts}</Typography>
              <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11 }}>payment entries</Typography>
            </Box>
            <Box className="chip">
              <Typography className="label">Outstanding Balance</Typography>
              <Typography className="value" sx={{ color: getBalanceColor(chipTotals.outstanding) }}>{formatCurrency(chipTotals.outstanding)}</Typography>
              <Typography sx={{ fontSize: fontSize.xsmall, color: colors.grey11 }}>
                {Math.abs(chipTotals.outstanding) < 0.01 ? "net ending across agents" : chipTotals.outstanding < 0 ? "net credit balance" : "net debit balance"}
              </Typography>
            </Box>
          </ChipStrip>

          <ReportBodyGrid sx={{ marginTop: gaps.medium1 }}>
            {groupBy === "agent" && (
              <NavRail>
                <Box className="nav-header">Agents</Box>
                {ledgerData.map((b) => (
                  <Box key={b.agent.agentID} className="nav-item" onClick={() => handleScrollToAgent(b.agent.agentID)}>
                    {b.agent.agentID} — {b.agent.name}
                  </Box>
                ))}
              </NavRail>
            )}

            <ReportMain>
              {groupedBlocks &&
                Object.entries(groupedBlocks).map(([groupKey, blocks]) => (
                  <Box key={groupKey || "agent-group"}>
                    {groupKey && (
                      <GroupHeaderRow>
                        <Typography sx={{ fontSize: fontSize.base, fontWeight: fontWeight.bold2 }}>
                          {groupKey}
                        </Typography>
                        <Box sx={{ display: "flex", gap: gaps.large1 }}>
                          <Typography sx={{ fontSize: fontSize.small }}>
                            {blocks.length} agent(s)
                          </Typography>
                          <Typography sx={{ fontSize: fontSize.small, color: colors.red01 }}>
                            Charges: {formatCurrency(blocks.reduce((s, b) => s + b.totalCharges, 0))}
                          </Typography>
                          <Typography sx={{ fontSize: fontSize.small, color: colors.green }}>
                            Payments: {formatCurrency(blocks.reduce((s, b) => s + b.totalPayments, 0))}
                          </Typography>
                          <Typography sx={{ fontSize: fontSize.small, color: getBalanceColor(blocks.reduce((s, b) => s + b.endBal, 0)) }}>
                            Balance: {formatCurrency(blocks.reduce((s, b) => s + b.endBal, 0))}
                          </Typography>
                        </Box>
                      </GroupHeaderRow>
                    )}
                    {blocks.map((block) => renderBlock(block))}
                  </Box>
                ))}
            </ReportMain>
          </ReportBodyGrid>

          {grandTotals && (
            <GrandTotalsRow>
              <Box className="item">
                <Typography className="label">Agents Shown</Typography>
                <Typography className="value">{grandTotals.agents}</Typography>
              </Box>
              <Box className="item">
                <Typography className="label">Beg Balance</Typography>
                <Typography className="value" sx={{ color: getBalanceColor(grandTotals.beginBal) }}>
                  {formatCurrency(grandTotals.beginBal)}
                </Typography>
              </Box>
              <Box className="item">
                <Typography className="label">Total Charges</Typography>
                <Typography className="value" sx={{ color: colors.red01 }}>
                  {formatCurrency(grandTotals.totalCharges)}
                </Typography>
              </Box>
              <Box className="item">
                <Typography className="label">Total Payments</Typography>
                <Typography className="value" sx={{ color: colors.green }}>
                  {formatCurrency(grandTotals.totalPayments)}
                </Typography>
              </Box>
              <Box className="item">
                <Typography className="label">Net Ending Balance</Typography>
                <Typography className="value" sx={{ color: getBalanceColor(grandTotals.endBal) }}>
                  {formatCurrency(grandTotals.endBal)}
                </Typography>
              </Box>
            </GrandTotalsRow>
          )}
        </>
      )}
    </Box>
  );
};

export default LedgerReportBody;
